import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { getRecentEvents } from '../../../api/verificationEventsApi';
import { formatDateTime } from '../../../utils/dateHelpers';
import { styles } from './EditCasesModal';

/**
 * Aggregate diagnostics modal — advisor only. Lists the last 20
 * verification_system_events across all files for spotting patterns
 * (e.g. every upload for the last 3 hours has failed with a 404
 * mentioning the model name).
 */
export default function AggregateDiagnosticsModal({ onClose }) {
  const [events, setEvents] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let mounted = true;
    getRecentEvents(20)
      .then((rows) => mounted && setEvents(rows))
      .catch((e) => mounted && setErr(e.message || 'Failed to load events.'));
    return () => { mounted = false; };
  }, []);

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <motion.div
        className="card"
        style={{ ...styles.modal, maxWidth: '720px' }}
        onClick={(e) => e.stopPropagation()}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        <h3 style={{ marginBottom: '0.5rem' }}>Recent verification errors</h3>
        <p style={{ color: 'var(--cp-text-muted)', fontSize: '0.85rem', marginBottom: '0.75rem' }}>
          Last 20 Anthropic API failures across all files. Useful for spotting
          patterns (e.g. a deprecated <code>VERIFICATION_MODEL</code> failing
          every upload).
        </p>
        {err && <p style={{ color: 'var(--cp-danger)', fontSize: '0.8rem' }}>{err}</p>}
        {events == null && !err && <p style={{ color: 'var(--cp-text-muted)', fontSize: '0.85rem' }}>Loading…</p>}
        {events && events.length === 0 && (
          <p style={{ color: 'var(--cp-text-muted)', fontSize: '0.85rem' }}>No events in the system.</p>
        )}
        {events && events.length > 0 && (
          <div style={{ maxHeight: '420px', overflowY: 'auto', border: '1px solid var(--cp-surface-alt)', borderRadius: '8px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
              <thead>
                <tr style={{ background: '#F7F9FC' }}>
                  <th style={{ padding: '0.4rem 0.6rem', textAlign: 'left' }}>Time</th>
                  <th style={{ padding: '0.4rem 0.6rem', textAlign: 'left' }}>Status</th>
                  <th style={{ padding: '0.4rem 0.6rem', textAlign: 'left' }}>Model</th>
                  <th style={{ padding: '0.4rem 0.6rem', textAlign: 'left' }}>Message</th>
                </tr>
              </thead>
              <tbody>
                {events.map((e) => (
                  <tr key={e.id} style={{ borderTop: '1px solid var(--cp-surface-alt)' }}>
                    <td style={{ padding: '0.4rem 0.6rem', whiteSpace: 'nowrap' }}>{formatDateTime(e.attemptedAt)}</td>
                    <td style={{ padding: '0.4rem 0.6rem' }}>{e.errorStatus ?? 'network'}</td>
                    <td style={{ padding: '0.4rem 0.6rem' }}><code>{e.modelUsed || '—'}</code></td>
                    <td style={{ padding: '0.4rem 0.6rem', color: 'var(--cp-text)' }}>{e.errorMessage || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.75rem' }}>
          <button className="btn btn-outline" onClick={onClose}>Close</button>
        </div>
      </motion.div>
    </div>
  );
}
